import React, { FC, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Dialog, Stack, type Theme, Typography, useTheme } from '@mui/material';
import { mergeSx } from 'merge-sx';

import { PlatformLocale, useGetCatalogItemDetails } from '@/api/generated';
import CheckIcon from '@/assets/icons/check.svg?react';
import BackdropLoader from '@/components/BackdropLoader';
import CommonDialogContent from '@/components/Dialog/CommonDialogContent';
import CommonDialogTitle from '@/components/Dialog/CommonDialogTitle';
import RichTextLabel from '@/components/RichTextLabel/RichTextLabel';
import { HorizontalTab, HorizontalTabs, TabPanel } from '@/components/Tabs';
import { useSnackbar } from '@/hooks/useSnackbar';
import { borders } from '@/theme/constants';
import typography from '@/theme/typography';

enum TestInformationTabs {
  GENERAL = 'GENERAL',
  SETTINGS = 'SETTINGS'
}
interface TestSpecificationItemProps {
  label?: string;
  value?: string | false;
  fallback?: string;
}

const TestSpecificationItem: FC<TestSpecificationItemProps> = ({ label, value, fallback }) => (
  <Stack
    gap={0.5}
    sx={{
      p: 1,
      bgcolor: 'primary.50',
      minWidth: 200
    }}
  >
    <Typography variant="body2" sx={{ color: 'grey.800' }}>
      {label}
    </Typography>
    <Typography variant="body2">{value || fallback || '-'}</Typography>
  </Stack>
);

interface TestInformationModalProps {
  onClose: () => void;
  catalogItemId: string;
  onButtonClick?: () => void;
  isAvailable?: boolean;
  isSelected?: boolean;
  readonly?: boolean;
}

const TestInformationModal: FC<TestInformationModalProps> = ({
  onClose,
  catalogItemId,
  onButtonClick,
  isAvailable,
  isSelected,
  readonly
}) => {
  const { t, i18n } = useTranslation(['processes', 'common']);
  const { spacing, palette } = useTheme();
  const { showMessage } = useSnackbar();
  const [tabValue, setTabValue] = useState(TestInformationTabs.GENERAL);

  const {
    data: catalogItem,
    isLoading,
    error
  } = useGetCatalogItemDetails(
    catalogItemId,
    { locale: i18n.language as PlatformLocale },
    {
      query: {
        enabled: !!catalogItemId
      }
    }
  );

  const handleTabChange = useCallback((_event: SyntheticEvent, newValue: TestInformationTabs) => {
    setTabValue(newValue);
  }, []);

  const handleAdd = useCallback(() => {
    onButtonClick?.();
    onClose?.();
  }, [onButtonClick, onClose]);

  const languages = useMemo(
    () =>
      catalogItem?.supportedLocales?.map(localeCode =>
        t(`common:common.languages.${localeCode}`, {
          ns: 'common'
        })
      ),
    [catalogItem?.supportedLocales, t]
  );

  const firstColumn = useMemo(() => catalogItem?.infoGrid?.slice(0, catalogItem?.infoGrid?.length / 2), [catalogItem]);
  const secondColumn = useMemo(() => catalogItem?.infoGrid?.slice(catalogItem?.infoGrid?.length / 2), [catalogItem]);

  useEffect(() => {
    if (!error) return;

    showMessage(t('common:error.somethingWentWrong'));
    onClose();
  }, [error, onClose, showMessage, t]);

  if (isLoading) {
    return <BackdropLoader />;
  }

  if (error || !catalogItem) {
    return null;
  }

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: spacing(1),
          minHeight: 400,
          bgcolor: 'background.default'
        }
      }}
    >
      <CommonDialogTitle onClose={onClose}>
        <Box flexGrow={1}>
          <Typography variant="h3" sx={{ fontWeight: typography.fontWeightMedium }}>
            {catalogItem.name}
          </Typography>
        </Box>
      </CommonDialogTitle>

      <Box px={3}>
        <HorizontalTabs value={tabValue} onChange={handleTabChange}>
          <HorizontalTab label={t('layouts.testInfo.tabs.generalInfo')} value={TestInformationTabs.GENERAL} />
        </HorizontalTabs>
      </Box>

      <CommonDialogContent>
        <TabPanel currentValue={tabValue} value={TestInformationTabs.GENERAL}>
          <Stack direction="row" gap={3} sx={{ height: '100%' }} m={3}>
            <Stack sx={{ flex: 1 }}>
              <Stack gap={3}>
                {(catalogItem.description || catalogItem.iconUrl) && (
                  <Stack direction="row" justifyContent="space-between">
                    <Stack gap={1}>
                      <Typography variant="body2" sx={{ fontWeight: typography.fontWeightMedium }}>
                        {t('layouts.testInfo.generalDetails')}
                      </Typography>
                      {catalogItem.description && (
                        <Typography variant="body2" sx={{ mb: 2, color: 'grey.700' }}>
                          <RichTextLabel html={catalogItem.description} />
                        </Typography>
                      )}
                    </Stack>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: 230,
                        height: 230,
                        minWidth: 230
                      }}
                    >
                      {!!catalogItem.iconUrl && (
                        <Box
                          component="img"
                          src={catalogItem.iconUrl}
                          alt={catalogItem.name}
                          sx={{ width: 230, height: 230, objectFit: 'contain' }}
                        />
                      )}
                    </Box>
                  </Stack>
                )}
                <Stack gap={1}>
                  <Typography variant="body2" sx={{ fontWeight: typography.fontWeightMedium }}>
                    {t('layouts.testInfo.specifications')}:
                  </Typography>
                  <Stack direction="row" gap={2} width="100%">
                    <Stack gap={2} flexWrap="wrap" width="50%">
                      <TestSpecificationItem
                        label={t('layouts.testInfo.testingTime')}
                        value={
                          !!catalogItem.estimatedDuration &&
                          t('layouts.testInfo.testingTimeValue', {
                            count: catalogItem.estimatedDuration
                          })
                        }
                      />
                      <TestSpecificationItem
                        label={t('layouts.testInfo.numberOfItems')}
                        value={
                          !!catalogItem.itemCount &&
                          t('layouts.testInfo.numberOfItemsValue', { count: catalogItem.itemCount })
                        }
                      />
                      {firstColumn?.map(({ label, caption }) => (
                        <TestSpecificationItem key={label} label={label} value={caption} />
                      ))}
                    </Stack>
                    <Stack gap={2} width="50%">
                      <TestSpecificationItem
                        label={t('layouts.testInfo.availableLanguages')}
                        value={
                          !!catalogItem?.supportedLocales &&
                          t('list', {
                            val: languages,
                            ns: 'common'
                          })
                        }
                      />

                      {secondColumn?.map(({ label, caption }) => (
                        <TestSpecificationItem key={label} label={label} value={caption} />
                      ))}
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </TabPanel>
      </CommonDialogContent>

      <Box
        sx={{
          p: spacing(2.5, 2, 2),
          borderTop: borders.default,
          borderColor: palette.grey[300],
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Button onClick={onClose} color="primary" variant="text">
          {t('common:actions.cancel')}
        </Button>
        {!readonly && (
          <Button
            variant={isSelected || !isAvailable ? 'outlined' : 'contained'}
            color="primary"
            fullWidth
            sx={mergeSx<Theme>(
              {
                p: spacing(0.5, 1.25),
                flexGrow: 0,
                width: 'auto',
                minWidth: 'auto'
              },
              isSelected && {
                border: borders.half,
                borderColor: 'grey.400',
                p: spacing(0.625, 1.25),
                '& .MuiButton-startIcon': {
                  marginInlineEnd: 1,
                  marginInlineStart: -0.5
                }
              }
            )}
            onClick={handleAdd}
            startIcon={isSelected && <CheckIcon />}
          >
            <Typography variant="body2" sx={{ fontWeight: typography.fontWeightMedium }}>
              {!isAvailable && t('settings:processSettings.form.card.orderButton')}
              {isAvailable && !isSelected && t('settings:processSettings.form.card.addButton')}
              {isAvailable && isSelected && t('settings:processSettings.form.card.addedButton')}
            </Typography>
          </Button>
        )}
      </Box>
    </Dialog>
  );
};

export default TestInformationModal;
